import React, { useEffect, useRef, useState } from "react";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import "./chronicBreakdownChart.scss";
import { useChronicBreakdownStore } from "@stores/chronicBreakdownStore";
import { Spin } from "antd";
import HealthemSpinner from "@components/healthemSpinner/healthemSpinner";
import reportService from "@services/report.service";
import { useNavigate } from "react-router-dom";
import { routeMap } from "@utils/constants";
import { isEmpty } from "lodash";
import DataNotFound from "@components/noDataFound";
import { NO_DATA_AVAILABLE } from "../../../../constants/constants";

interface TooltipData {
  name: string;
  totalPatientCount: number;
  percentage: number;
  percentageLabel: any;
}

const BarChart: React.FC = () => {
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const chartData = useChronicBreakdownStore((state) => state.chartData);
  const chartLoading = useChronicBreakdownStore((state) => state.chartLoading);
  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null);
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false);
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const prevAppliedFilters = useChronicBreakdownStore((state) => state.appliedProvider);
  const patientIdsHandler = useChronicBreakdownStore((state) => state.patientIdsHandler);
  const memberPageNavigationHandler = useChronicBreakdownStore((state) => state.memberPageNavigationHandler);
  const setChronicName = useChronicBreakdownStore((state) => state.setChronicName);

  const navigate = useNavigate();
  const conditions = Array.isArray(chartData?.conditions) ? chartData.conditions : [];
  const sortedConditions = [...conditions].sort((a, b) => b.percentage - a.percentage);
  const maxPercentage = sortedConditions.length ? sortedConditions[0].percentage : 0;

  const hideTooltipWithDelay = () => {
    hideTimeoutRef.current = setTimeout(() => {
      const isBarHovered = chartContainerRef.current?.querySelector(".apexcharts-bar-area:hover");
      const isTooltipHovered = tooltipRef.current?.matches(":hover");

      if (!isBarHovered && !isTooltipHovered) {
        setTooltipVisible(false);
      }
    }, 0);
  };

  const cancelHideTooltip = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const handleTooltipClick = (name: any) => {
    setShowLoader(true);
    const [state, city, healthPlan, pcpVendor, provider] = prevAppliedFilters;

    reportService.getChronicBreakdownReportPatientIds(state, city, healthPlan, pcpVendor, provider, name).subscribe({
      next: (response) => {
        patientIdsHandler(response?.data?.data);
        setChronicName(name);
        memberPageNavigationHandler(true);
        setShowLoader(true);
        navigate(routeMap["members"]);
        setShowLoader(false);
      },
      error: (error) => {
        console.error(error);
        setShowLoader(false);
      },
    });
  };

  useEffect(() => {
    const handleGlobalMouseMove = (event: MouseEvent) => {
      const tooltipEl = tooltipRef.current;
      const chartEl = chartContainerRef.current;

      const isInsideTooltip = tooltipEl?.contains(event.target as Node);
      const isInsideChart = chartEl?.querySelector(".apexcharts-bar-area:hover");

      if (!isInsideTooltip && !isInsideChart) {
        setTooltipVisible(false);
      }
    };

    const handleScroll = () => {
      setTooltipVisible(false);
    };

    document.addEventListener("mousemove", handleGlobalMouseMove);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("mousemove", handleGlobalMouseMove);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  const options: ApexOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      events: {
        dataPointMouseEnter: (event: MouseEvent, chartContext: any, config: any) => {
          cancelHideTooltip();
          const { dataPointIndex } = config;
          const condition = sortedConditions[dataPointIndex];
          const barElement = (event.target as HTMLElement).closest(".apexcharts-bar-area") as HTMLElement;

          if (chartContainerRef.current && barElement) {
            const containerEl = chartContainerRef.current;
            const chartEl = containerEl.closest(".chronic-chart") as HTMLElement;

            const chartRect = chartEl.getBoundingClientRect();
            const barRect = barElement.getBoundingClientRect();

            const relativeX = barRect.left + barRect.width / 2 - chartRect.left;
            const relativeY = barRect.top - chartRect.top;

            setPosition({ left: relativeX, top: relativeY });
            setTooltipData(condition);
            setTooltipVisible(true);
          }
        },

        dataPointMouseLeave: hideTooltipWithDelay,
      },
      animations: { enabled: false },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: "50%",
        borderRadius: 4,
        dataLabels: {
          position: "center",
        },
      },
    },
    colors: ["#807AF4"],
    dataLabels: {
      enabled: true,
      offsetX: 0,
      formatter: function (_val: number, opts: any) {
        return opts.w.config.series[0].data[opts.dataPointIndex].label;
      },
      style: {
        colors: ["#fff"],
      },
      textAnchor: "middle",
    },
    yaxis: {
      labels: {
        minWidth: 150,
        maxWidth: 300,
        offsetX: -10,
      },
    },
    xaxis: {
      categories: sortedConditions.map((condition) => condition.name),
      title: {
        text: "",
      },
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
      min: 0,
      max: Math.ceil(maxPercentage * 1.1),
    },
    tooltip: {
      enabled: false,
    },
    grid: {
      show: false,
      padding: {
        left: 20,
      },
    },
    states: {
      hover: {
        filter: {
          type: "none",
        },
      },
      active: {
        filter: {
          type: "none",
        },
      },
    },
  };

  const series = [
    {
      name: "Percentage",
      data: sortedConditions.map((c) => {
        const adjustedPercentage = c.percentage < 1 ? 7.5 : c.percentage + 7;
        return {
          x: c.name,
          y: adjustedPercentage,
          actualPercentage: c.percentage,
          label: c.percentageLabel,
          totalPatientCount: c.totalPatientCount,
        };
      }),
    },
  ];

  return (
    <Spin spinning={chartLoading} indicator={<HealthemSpinner />}>
      <div className="chronic-chart">
        <div className="chronic-chart__header mt-1 mr-1 ml-1 mb-1 f500">
          <span>Members By Chronic Conditions</span>
          <span className="italic">Total Members: {chartData?.totalCount ? chartData.totalCount.toLocaleString() : 0}</span>
        </div>
        {isEmpty(chartData?.conditions) ? (
          <div className="chronic-chart__body">
            <DataNotFound message={NO_DATA_AVAILABLE} />
          </div>
        ) : (
          <>
            <div className="chronic-chart__body" ref={chartContainerRef}>
              <Chart options={options} series={series} type="bar" height={conditions.length * 35} width={700} />
            </div>
            {tooltipVisible && tooltipData && (
              <div ref={tooltipRef} className="custom-tooltip-with-arrow" style={{ left: position.left, top: position.top }} onMouseEnter={cancelHideTooltip} onMouseLeave={hideTooltipWithDelay}>
                <Spin spinning={showLoader} indicator={<HealthemSpinner />}>
                  <div className="tooltip-header f500">
                    <strong className="mr-05">{tooltipData.percentageLabel}</strong>
                    <span onClick={() => handleTooltipClick(tooltipData.name)}>
                    {(tooltipData.totalPatientCount).toLocaleString()} 
                      <i className="icon icon-external-link ml-02" />
                    </span>
                  </div>
                  <div className="tooltip-name">{tooltipData.name}</div>
                </Spin>
              </div>
            )}
          </>
        )}
      </div>
    </Spin>
  );
};

export default BarChart;